import axios, { AxiosError, AxiosInstance } from 'axios'
import { toast } from '@/components/ui/toaster'
import type {
  User,
  Subscription,
  Plan,
  Product,
  Asset,
  AssetDetail,
  AssetDownloadResult,
  GenerationResult,
  Task,
  ReferenceAd,
  ScoreResult,
  PaginatedResponse,
  ApiError,
  CreateProductForm,
  CreateProductFromUrlPayload,
  GenerateImageForm,
  GenerateVideoForm,
  CompetitorResearchForm,
  VariationGenerateForm,
  ForeplaySearchResult,
  ScoredAd,
  VariationAnalyzeForm,
  VariationGenerateFormV2,
  VariationAnalyzeResponse,
  VariationGenerateBatchResponse,
  VariationAnalysisDetail,
  AssetStats,
  CreditPackage,
  CreditPurchase,
  Idea,
  IdeaCreate,
  IdeaInput,
  ResearchResult,
  ResearchStartRequestPayload,
  ResearchStartResponse,
  ResearchResultPayload,
  SaveResearchReportResponse,
  DeleteResearchReportResponse,
  ResearchReportSummary,
  ResearchReportDetail,
  ReportMarkdownResponse,
  ForeplayAPIAd,
  ForeplayAPIBrand,
  PerplexitySearchResult,
  PerplexityChatResult,
  NovelParseResult,
  NovelScenariosPayload,
  VerticalSkillMediaType,
  VerticalSkillOptionsResponse,
  GenerateNovelScenariosRequestPayload,
  ConfirmNovelScenariosRequestPayload,
  VerticalIdeasStartRequestPayload,
  VerticalIdeasStartResponse,
  VerticalIdeasResultPayload,
  DigitalHumanForm,
  FeedbackBatch,
  FeedbackBatchAssets,
  FeedbackBatchStats,
  FeedbackReview,
  ReviewUpdate,
  DownloadResult,
  FinanceReport,
  GenerateFinanceReportsResponse,
  MaterialAnalyticsSummary,
  MaterialAnalyticsAssetListResponse,
  MaterialAnalyticsChainItem,
  MaterialAnalyticsImageSearchResult,
  MaterialAnalyticsProduct,
  ReviewBatchSummary,
  ReviewBatchDetail,
  BatchReviewsPayload,
  VariantFeedback,
  VariantReviewInput,
} from '@/types'

const BASE_URL = import.meta.env.VITE_API_URL || '/api/v1'

class ApiClient {
  // Exposed so sibling modules under lib/api/ can reuse the configured
  // axios instance (baseURL + auth interceptor + token refresh) without
  // creating a second client.
  public client: AxiosInstance
  private isRefreshing = false
  private refreshSubscribers: Array<(token: string) => void> = []

  constructor() {
    this.client = axios.create({
      baseURL: BASE_URL,
      timeout: 300000, // 5 minutes - Scoring may take 2-3 minutes for downloading + scoring
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Add auth token to requests
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    })

    // Handle errors with token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ApiError>) => {
        const originalRequest = error.config

        if (error.response?.status === 401 && originalRequest) {
          // Check error format - support both Kong and backend formats
          const errorCode = error.response.data?.error?.code
          const kongMessage = (error.response.data as { message?: string })?.message
          const isLoginEndpoint = originalRequest.url?.includes('/auth/login')
          const isRefreshEndpoint = originalRequest.url?.includes('/auth/refresh')

          // Detect token expiration - compatible with Kong and backend
          const isTokenExpired =
            errorCode === 'TOKEN_EXPIRED' ||
            kongMessage === 'Token expired'

          const isUnauthorized =
            errorCode === 'UNAUTHORIZED' ||
            kongMessage?.includes('Missing') ||
            kongMessage?.includes('Invalid') ||
            kongMessage?.includes('Unauthorized')

          // Don't retry refresh endpoint or login endpoint
          if (isRefreshEndpoint || isLoginEndpoint) {
            return Promise.reject(error)
          }

          // Try to refresh token for expired or unauthorized errors
          if (isTokenExpired || isUnauthorized) {
            // Check if we have a refresh token
            const refreshToken = localStorage.getItem('refresh_token')
            if (!refreshToken) {
              this.clearTokensAndRedirect()
              return Promise.reject(error)
            }

            // If already refreshing, queue this request
            if (this.isRefreshing) {
              return new Promise((resolve) => {
                this.refreshSubscribers.push((newToken: string) => {
                  originalRequest.headers.Authorization = `Bearer ${newToken}`
                  resolve(this.client.request(originalRequest))
                })
              })
            }

            this.isRefreshing = true

            try {
              const refreshed = await this.tryRefreshToken()
              if (refreshed) {
                const newToken = localStorage.getItem('access_token')!
                // Notify all queued requests
                this.refreshSubscribers.forEach((callback) => callback(newToken))
                this.refreshSubscribers = []
                // Retry the original request
                originalRequest.headers.Authorization = `Bearer ${newToken}`
                return this.client.request(originalRequest)
              } else {
                this.clearTokensAndRedirect()
              }
            } catch {
              this.clearTokensAndRedirect()
            } finally {
              this.isRefreshing = false
            }
          }
        }
        return Promise.reject(error)
      }
    )
  }

  private async tryRefreshToken(): Promise<boolean> {
    const refreshToken = localStorage.getItem('refresh_token')
    if (!refreshToken) return false

    try {
      // Use axios directly to avoid interceptor loop
      const response = await axios.post<{
        access_token: string
        refresh_token?: string
        expires_in: number
      }>(`${BASE_URL}/auth/refresh`, { refresh_token: refreshToken })

      localStorage.setItem('access_token', response.data.access_token)
      if (response.data.refresh_token) {
        localStorage.setItem('refresh_token', response.data.refresh_token)
      }
      return true
    } catch {
      return false
    }
  }

  private clearTokensAndRedirect() {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('auth-storage')
    toast({
      title: 'Session expired',
      description: 'Please log in again',
      variant: 'destructive',
    })
    setTimeout(() => {
      window.location.href = import.meta.env.BASE_URL + 'login'
    }, 1500)
  }

  // Auth
  async login(email: string, password: string) {
    const response = await this.client.post<{
      access_token: string
      refresh_token?: string
      token_type: string
      expires_in: number
    }>('/auth/login', { email, password })
    localStorage.setItem('access_token', response.data.access_token)
    if (response.data.refresh_token) {
      localStorage.setItem('refresh_token', response.data.refresh_token)
    }
    return response.data
  }

  async register(email: string, password: string, username?: string) {
    const response = await this.client.post<{
      access_token: string | null
      token_type: string
      expires_in: number
      verification_required: boolean
      email: string | null
    }>('/auth/register', { email, password, username })

    // Only set token if verification is not required (mock mode)
    if (!response.data.verification_required && response.data.access_token) {
      localStorage.setItem('access_token', response.data.access_token)
    }
    return response.data
  }

  async confirmEmail(email: string, code: string) {
    const response = await this.client.post<{
      success: boolean
      message: string
    }>('/auth/confirm-email', { email, code })
    // Note: No token returned - user must login after email confirmation
    return response.data
  }

  async resendVerificationCode(email: string): Promise<void> {
    await this.client.post('/auth/resend-code', { email })
  }

  async logout() {
    await this.client.post('/auth/logout')
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
  }

  async getMe(): Promise<User> {
    const response = await this.client.get<User>('/auth/me')
    return response.data
  }

  async forgotPassword(email: string): Promise<void> {
    await this.client.post('/auth/forgot-password', { email })
  }

  async resetPassword(email: string, code: string, newPassword: string): Promise<void> {
    await this.client.post('/auth/reset-password', {
      email,
      code,
      new_password: newPassword,
    })
  }

  async verifyEmail(token: string): Promise<void> {
    await this.client.post('/auth/verify-email', { token })
  }

  async googleLogin(idToken: string) {
    const response = await this.client.post<{
      access_token: string
      refresh_token?: string
      token_type: string
      expires_in: number
    }>('/auth/google-login', { id_token: idToken })
    localStorage.setItem('access_token', response.data.access_token)
    if (response.data.refresh_token) {
      localStorage.setItem('refresh_token', response.data.refresh_token)
    }
    return response.data
  }

  // Users
  async updateProfile(username: string): Promise<User> {
    const response = await this.client.patch<User>('/users/me', { username })
    return response.data
  }

  async completeOnboarding(data: {
    global_rule?: Record<string, unknown>
    preferences?: Record<string, unknown>
  }): Promise<User> {
    const response = await this.client.post<User>('/users/me/onboarding', data)
    return response.data
  }

  // Subscription
  async getSubscription(): Promise<Subscription> {
    const response = await this.client.get<Subscription>('/subscription')
    return response.data
  }

  async getPlans(): Promise<Plan[]> {
    const response = await this.client.get<Plan[]>('/subscription/plans')
    return response.data
  }

  async createCheckoutSession(
    planType: string,
    successUrl: string,
    cancelUrl: string
  ): Promise<{ checkout_url: string; session_id: string }> {
    const response = await this.client.post<{
      checkout_url: string
      session_id: string
    }>('/payment/checkout', {
      plan_type: planType,
      success_url: successUrl,
      cancel_url: cancelUrl,
    })
    return response.data
  }

  async cancelSubscription(): Promise<Subscription> {
    const response = await this.client.post<Subscription>('/subscription/cancel')
    return response.data
  }

  async reactivateSubscription(): Promise<Subscription> {
    const response = await this.client.post<Subscription>('/subscription/reactivate')
    return response.data
  }

  async getPaymentHistory(): Promise<{
    payments: Array<{
      id: string
      amount: number
      currency: string
      status: 'succeeded' | 'failed' | 'pending'
      description: string
      created_at: string
      invoice_url?: string
    }>
  }> {
    const response = await this.client.get('/payment/history')
    return response.data
  }

  async getCreditsUsage(days: number = 30) {
    const response = await this.client.get(`/subscription/credits/usage?days=${days}`)
    return response.data
  }

  // Products
  async getProducts(
    offset = 0,
    limit = 20,
    options?: { includeScreenshotUrls?: boolean }
  ): Promise<PaginatedResponse<Product>> {
    const queryParams = new URLSearchParams({
      offset: String(offset),
      limit: String(limit),
    })
    if (options?.includeScreenshotUrls !== undefined) {
      queryParams.set('include_screenshot_urls', String(options.includeScreenshotUrls))
    }
    const response = await this.client.get<PaginatedResponse<Product>>(
      `/products?${queryParams.toString()}`
    )
    return response.data
  }

  async getProduct(id: string): Promise<Product> {
    const response = await this.client.get<Product>(`/products/${id}`)
    return response.data
  }

  async getVerticalSkillOptions(
    productId: string,
    mediaType: VerticalSkillMediaType
  ): Promise<VerticalSkillOptionsResponse> {
    const response = await this.client.get<VerticalSkillOptionsResponse>(
      `/vertical-skills/options?product_id=${productId}&media_type=${mediaType}`
    )
    return response.data
  }

  async createProduct(data: CreateProductForm): Promise<Product> {
    const response = await this.client.post<Product>('/products', data)
    return response.data
  }

  async createProductFromUrl(
    url: string,
    productType?: CreateProductFromUrlPayload['product_type']
  ): Promise<Product> {
    const payload: CreateProductFromUrlPayload = { url }
    if (productType) {
      payload.product_type = productType
    }
    const response = await this.client.post<Product>('/products/from-url', payload)
    return response.data
  }

  async updateProduct(id: string, data: Partial<CreateProductForm>): Promise<Product> {
    const response = await this.client.patch<Product>(`/products/${id}`, data)
    return response.data
  }

  async deleteProduct(id: string): Promise<void> {
    await this.client.delete(`/products/${id}`)
  }

  async regenerateKeywords(productId: string): Promise<{ keywords: string[]; competitor_brands: string[] }> {
    const response = await this.client.post<{ keywords: string[]; competitor_brands: string[] }>(`/products/${productId}/regenerate-keywords`)
    return response.data
  }

  async uploadProductScreenshot(productId: string, file: File): Promise<Product> {
    const formData = new FormData()
    formData.append('file', file)
    const response = await this.client.post<Product>(`/products/${productId}/screenshot`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  }

  async parseVerticalNovelUrl(sourceUrl: string): Promise<NovelParseResult> {
    const response = await this.client.post<NovelParseResult>('/creative-lab/vertical-ideas/novel/parse', {
      source_mode: 'url',
      source_url: sourceUrl,
    })
    return response.data
  }

  async parseVerticalNovelText(rawText: string): Promise<NovelParseResult> {
    const response = await this.client.post<NovelParseResult>('/creative-lab/vertical-ideas/novel/parse', {
      source_mode: 'text',
      raw_text: rawText,
    })
    return response.data
  }

  async parseVerticalNovelFile(file: File): Promise<NovelParseResult> {
    const formData = new FormData()
    formData.append('source_mode', 'file')
    formData.append('file', file)
    const response = await this.client.post<NovelParseResult>('/creative-lab/vertical-ideas/novel/parse', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  }

  async removeVerticalIdeaMarketAnalysis(productId: string): Promise<{ success: boolean; message?: string | null }> {
    const response = await this.client.delete<{ success: boolean; message?: string | null }>(
      `/creative-lab/vertical-ideas/products/${productId}/market-analysis`
    )
    return response.data
  }

  async generateVerticalNovelScenarios(
    productId: string,
    data: GenerateNovelScenariosRequestPayload
  ): Promise<NovelScenariosPayload> {
    const response = await this.client.post<NovelScenariosPayload>(
      `/creative-lab/vertical-ideas/products/${productId}/novel/scenarios/generate`,
      data
    )
    return response.data
  }

  async confirmVerticalNovelScenarios(
    productId: string,
    data: ConfirmNovelScenariosRequestPayload
  ): Promise<NovelScenariosPayload> {
    const response = await this.client.put<NovelScenariosPayload>(
      `/creative-lab/vertical-ideas/products/${productId}/novel/scenarios/confirmed`,
      data
    )
    return response.data
  }

  async deleteVerticalNovelScenario(productId: string, scenarioId: string): Promise<NovelScenariosPayload> {
    const response = await this.client.delete<NovelScenariosPayload>(
      `/creative-lab/vertical-ideas/products/${productId}/novel/scenarios/${scenarioId}`
    )
    return response.data
  }

  async startVerticalIdeas(data: VerticalIdeasStartRequestPayload): Promise<VerticalIdeasStartResponse> {
    const response = await this.client.post<VerticalIdeasStartResponse>('/creative-lab/vertical-ideas/start', data)
    return response.data
  }

  async getVerticalIdeasResult(taskId: string): Promise<VerticalIdeasResultPayload> {
    const response = await this.client.get<VerticalIdeasResultPayload>(`/creative-lab/vertical-ideas/${taskId}/result`)
    return response.data
  }

  // Assets
  async getAssets(params: {
    product_id?: string
    requirement_id?: string
    asset_type?: string
    source_type?: string
    is_favorited?: boolean
    created_after?: string
    created_before?: string
    offset?: number
    limit?: number
    include_original_urls?: boolean
  }): Promise<PaginatedResponse<Asset>> {
    const queryParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) queryParams.append(key, String(value))
    })
    const response = await this.client.get<PaginatedResponse<Asset>>(
      `/assets?${queryParams.toString()}`
    )
    return response.data
  }

  async getAssetStats(productId?: string, requirementId?: string): Promise<AssetStats> {
    const query = new URLSearchParams()
    if (productId) query.set('product_id', productId)
    if (requirementId) query.set('requirement_id', requirementId)
    const params = query.toString() ? `?${query.toString()}` : ''
    const response = await this.client.get<{
      screenshot_count: number
      uploaded_count: number
      generated_count: number
      reference_count: number
      feedback_reference_count: number
      feedback_variant_count: number
      total_count: number
      image_count: number
      video_count: number
    }>(`/assets/stats${params}`)
    const d = response.data
    return {
      screenshot: d.screenshot_count,
      uploaded: d.uploaded_count,
      generated: d.generated_count,
      reference: d.reference_count,
      feedback_reference: d.feedback_reference_count,
      feedback_variant: d.feedback_variant_count,
      total: d.total_count,
      image: d.image_count,
      video: d.video_count,
    }
  }

  async getAsset(id: string): Promise<AssetDetail> {
    const response = await this.client.get<AssetDetail>(`/assets/${id}`)
    return response.data
  }

  async recordAssetDownload(id: string): Promise<AssetDownloadResult> {
    const response = await this.client.post<AssetDownloadResult>(`/assets/${id}/download`)
    return response.data
  }

  async uploadAsset(file: File, productId?: string, description?: string, requirementId?: string): Promise<Asset> {
    const formData = new FormData()
    formData.append('file', file)
    if (productId) formData.append('product_id', productId)
    if (description) formData.append('description', description)
    if (requirementId) formData.append('requirement_id', requirementId)

    const response = await this.client.post<Asset>('/assets', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  }

  async uploadAssetWithProgress(
    file: File,
    productId?: string,
    description?: string,
    onProgress?: (progress: number) => void,
    requirementId?: string,
  ): Promise<Asset> {
    const formData = new FormData()
    formData.append('file', file)
    if (productId) formData.append('product_id', productId)
    if (description) formData.append('description', description)
    if (requirementId) formData.append('requirement_id', requirementId)

    const response = await this.client.post<Asset>('/assets', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(percentCompleted)
        }
      },
    })
    return response.data
  }

  async deleteAsset(id: string): Promise<void> {
    await this.client.delete(`/assets/${id}`)
  }

  async bulkDeleteAssets(ids: string[]): Promise<{ message: string }> {
    const response = await this.client.post<{ message: string }>('/assets/bulk-delete', ids)
    return response.data
  }

  async toggleFavorite(id: string): Promise<Asset> {
    const response = await this.client.post<Asset>(`/assets/${id}/favorite`)
    return response.data
  }

  async scoreAsset(id: string, forceRefresh = false): Promise<ScoreResult> {
    const response = await this.client.post<ScoreResult>(
      `/assets/${id}/score?force_refresh=${forceRefresh}`
    )
    return response.data
  }

  // Generation
  async generateImage(data: GenerateImageForm): Promise<Task> {
    const response = await this.client.post<Task>('/generate/image', data)
    return response.data
  }

  async generateVideo(data: GenerateVideoForm): Promise<Task> {
    const payload = data.slots ? data : { ...data, slots: [null] }
    const response = await this.client.post<Task | { tasks: Task[] }>('/generate/video', payload)
    const maybeTasks = response.data as { tasks?: Task[] }
    if (Array.isArray(maybeTasks.tasks)) {
      if (maybeTasks.tasks.length === 0) {
        throw new Error('No task returned from /generate/video')
      }
      return maybeTasks.tasks[0]
    }
    return response.data as Task
  }

  async generateCompetitorResearch(data: CompetitorResearchForm): Promise<Task> {
    const response = await this.client.post<Task>('/generate/competitor-research', data)
    return response.data
  }

  async generateVariation(data: VariationGenerateForm): Promise<Task> {
    const response = await this.client.post<Task>('/generate/variation', data)
    return response.data
  }

  async generateDigitalHuman(data: DigitalHumanForm): Promise<Task> {
    const response = await this.client.post<Task>('/generate/digital-human', data)
    return response.data
  }

  async searchReferenceAds(params: {
    keywords?: string
    brands?: string
    offset?: number
    limit?: number
  }): Promise<{ ads: ReferenceAd[]; total: number; offset: number; limit: number }> {
    const queryParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) queryParams.append(key, String(value))
    })
    const response = await this.client.get(`/generate/reference-ads?${queryParams.toString()}`)
    return response.data
  }

  // Foreplay search (v2 - dual route)
  async searchReferenceAdsV2(params: {
    description: string
    cate?: string
    keywords?: string[]
    competitor_brands?: string[]
    media_type?: 'image' | 'video'
    limit?: number
  }): Promise<ForeplaySearchResult> {
    const response = await this.client.post<ForeplaySearchResult>('/generate/search-reference-ads', params)
    return response.data
  }

  // Score reference ads
  async scoreReferenceAds(params: {
    ad_ids: string[]
    product_id?: string
    product_info?: Record<string, unknown>
    top_n?: number
  }): Promise<{ scored_ads: ScoredAd[]; total: number }> {
    const response = await this.client.post<{ scored_ads: ScoredAd[]; total: number }>(
      '/generate/score-reference-ads',
      params
    )
    return response.data
  }

  // Tasks
  async getTasks(params: {
    task_type?: string
    task_status?: string
    product_id?: string
    offset?: number
    limit?: number
  }): Promise<PaginatedResponse<Task>> {
    const queryParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) queryParams.append(key, String(value))
    })
    const response = await this.client.get<PaginatedResponse<Task>>(
      `/tasks?${queryParams.toString()}`
    )
    return response.data
  }

  async getTask(id: string): Promise<Task> {
    const response = await this.client.get<Task>(`/tasks/${id}`)
    return response.data
  }

  async getTaskResults(id: string): Promise<GenerationResult[]> {
    const response = await this.client.get<GenerationResult[]>(`/tasks/${id}/results`)
    return response.data
  }

  async cancelTask(id: string): Promise<Task> {
    const response = await this.client.post<Task>(`/tasks/${id}/cancel`)
    return response.data
  }

  async retryTask(id: string): Promise<Task> {
    const response = await this.client.post<Task>(`/tasks/${id}/retry`)
    return response.data
  }

  // ========== Two-Stage Variation Generation ==========

  /**
   * Stage 1: Analyze source image and get variation directions.
   * No credits are consumed for analysis.
   */
  async analyzeForVariations(params: VariationAnalyzeForm): Promise<VariationAnalyzeResponse> {
    const response = await this.client.post<VariationAnalyzeResponse>(
      '/variations/analyze',
      params
    )
    return response.data
  }

  /**
   * Stage 2: Generate variations based on selected directions.
   * Consumes N credits for N selected directions.
   */
  async generateVariationV2(params: VariationGenerateFormV2): Promise<VariationGenerateBatchResponse> {
    const response = await this.client.post<VariationGenerateBatchResponse>(
      '/variations/generate',
      params
    )
    return response.data
  }

  /**
   * Get analysis details with all generated variations.
   */
  async getVariationAnalysis(analysisId: string): Promise<VariationAnalysisDetail> {
    const response = await this.client.get<VariationAnalysisDetail>(
      `/variations/${analysisId}`
    )
    return response.data
  }

  // ========== Credit Purchase ==========

  /**
   * Get available credit packages for purchase.
   */
  async getCreditPackages(): Promise<CreditPackage[]> {
    const response = await this.client.get<CreditPackage[]>('/credits/packages')
    return response.data
  }

  /**
   * Create a credit purchase and get Stripe PaymentIntent client_secret.
   * @param packageId - Credit package ID (e.g., 'pack_15')
   * @param quantity - Number of packages to purchase (1-10, default 1)
   */
  async createCreditPurchase(packageId: string, quantity: number = 1): Promise<{
    client_secret: string
    purchase_id: string
  }> {
    const response = await this.client.post<{
      client_secret: string
      purchase_id: string
    }>('/credits/purchase', { package_id: packageId, quantity })
    return response.data
  }

  /**
   * Get credit purchase history for the current user.
   */
  async getCreditPurchases(limit = 50, offset = 0): Promise<CreditPurchase[]> {
    const response = await this.client.get<CreditPurchase[]>(
      `/credits/purchases?limit=${limit}&offset=${offset}`
    )
    return response.data
  }

  // ========== Creative Lab - Ideas CRUD ==========

  async createIdea(productId: string, data: IdeaCreate): Promise<Idea> {
    const response = await this.client.post<Idea>(`/products/${productId}/ideas`, data)
    return response.data
  }

  async getIdeas(productId: string, params: {
    offset?: number
    limit?: number
    source?: string
    favorited?: boolean
    tag?: string
    sort?: string
  } = {}): Promise<PaginatedResponse<Idea>> {
    const queryParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) queryParams.append(key, String(value))
    })
    const response = await this.client.get<PaginatedResponse<Idea>>(
      `/products/${productId}/ideas?${queryParams.toString()}`
    )
    return response.data
  }

  async getIdea(ideaId: string): Promise<Idea> {
    const response = await this.client.get<Idea>(`/ideas/${ideaId}`)
    return response.data
  }

  async updateIdea(ideaId: string, data: Partial<IdeaCreate>): Promise<Idea> {
    const response = await this.client.put<Idea>(`/ideas/${ideaId}`, data)
    return response.data
  }

  async toggleIdeaFavorite(ideaId: string, isFavorited: boolean): Promise<Idea> {
    const response = await this.client.put<Idea>(`/ideas/${ideaId}/favorite`, {
      is_favorited: isFavorited,
    })
    return response.data
  }

  async deleteIdea(ideaId: string): Promise<void> {
    await this.client.delete(`/ideas/${ideaId}`)
  }

  async bulkDeleteIdeas(ideaIds: string[]): Promise<{ message: string }> {
    const response = await this.client.post<{ message: string }>('/ideas/bulk-delete', ideaIds)
    return response.data
  }

  // ========== Creative Lab - Research ==========

  async researchIdeas(productId: string, tools?: string[]): Promise<ResearchResult> {
    const response = await this.client.post<ResearchResult>('/creative-lab/research', {
      product_id: productId,
      tools,
    })
    return response.data
  }

  async startCreativeLabResearch(payload: ResearchStartRequestPayload): Promise<ResearchStartResponse> {
    const response = await this.client.post<ResearchStartResponse>(
      '/creative-lab/research/start',
      payload
    )
    return response.data
  }

  async getCreativeLabResearchResult(taskId: string): Promise<ResearchResultPayload> {
    const response = await this.client.get<ResearchResultPayload>(
      `/creative-lab/research/${taskId}/result`
    )
    return response.data
  }

  async saveCreativeLabResearchReport(taskId: string): Promise<SaveResearchReportResponse> {
    const response = await this.client.post<SaveResearchReportResponse>(
      `/creative-lab/research/${taskId}/report/save`
    )
    return response.data
  }

  async listCreativeLabResearchReports(
    productId: string,
    offset = 0,
    limit = 20
  ): Promise<PaginatedResponse<ResearchReportSummary>> {
    const query = new URLSearchParams({
      product_id: productId,
      offset: String(offset),
      limit: String(limit),
    })
    const response = await this.client.get<PaginatedResponse<ResearchReportSummary>>(
      `/creative-lab/research/reports?${query.toString()}`
    )
    return response.data
  }

  async getCreativeLabResearchReport(reportId: string): Promise<ResearchReportDetail> {
    const response = await this.client.get<ResearchReportDetail>(
      `/creative-lab/research/reports/${reportId}`
    )
    return response.data
  }

  async getCreativeLabResearchReportMarkdown(reportId: string): Promise<ReportMarkdownResponse> {
    const response = await this.client.get<ReportMarkdownResponse>(
      `/creative-lab/research/reports/${reportId}/markdown`
    )
    return response.data
  }

  async deleteCreativeLabResearchReport(reportId: string): Promise<DeleteResearchReportResponse> {
    const response = await this.client.delete<DeleteResearchReportResponse>(
      `/creative-lab/research/reports/${reportId}`
    )
    return response.data
  }

  // ========== Creative Lab - Foreplay Browse ==========

  async foreplaySearchAds(params: {
    query: string
    display_format?: string[]
    publisher_platform?: string[]
    running_duration_min_days?: number
    first_seen_start?: string
    first_seen_end?: string
    order?: string
    limit?: number
    cursor?: string
  }): Promise<{ ads: ForeplayAPIAd[]; cursor: string | number | null; total: number }> {
    const response = await this.client.post('/creative-lab/foreplay/search-ads', params)
    return response.data
  }

  async foreplaySearchBrands(params: {
    query: string
    limit?: number
  }): Promise<{ brands: ForeplayAPIBrand[] }> {
    const response = await this.client.post('/creative-lab/foreplay/search-brands', params)
    return response.data
  }

  async foreplayBrandAds(params: {
    brand_ids: string[]
    display_format?: string[]
    publisher_platform?: string[]
    limit?: number
    order?: string
    cursor?: string
  }): Promise<{ ads: ForeplayAPIAd[]; cursor: string | number | null; total: number }> {
    const response = await this.client.post('/creative-lab/foreplay/brand-ads', params)
    return response.data
  }

  // ========== Creative Lab - Perplexity Browse ==========

  async perplexitySearch(params: {
    query: string
    max_results?: number
    country?: string
    domain_filter?: string[]
  }): Promise<PerplexitySearchResult> {
    const response = await this.client.post<PerplexitySearchResult>(
      '/creative-lab/perplexity/search',
      params
    )
    return response.data
  }

  async perplexityChat(params: {
    message: string
    model?: string
    return_images?: boolean
    return_videos?: boolean
  }): Promise<PerplexityChatResult> {
    const response = await this.client.post<PerplexityChatResult>(
      '/creative-lab/perplexity/chat',
      params
    )
    return response.data
  }

  // ========== Creative Lab - Tags ==========

  async getCreativeLabTags(): Promise<string[]> {
    const response = await this.client.get<{ tags: string[] }>('/creative-lab/tags')
    return response.data.tags
  }

  // ========== Creative Lab - Idea Mode Generation ==========

  async generateImageWithIdeas(data: {
    slots: (IdeaInput | null)[]
    prompt?: string
    model: string
    aspect_ratio?: string
    image_size?: string
    product_id?: string
    include_product_screenshot?: boolean
    reference_asset_id?: string
    reference_asset_ids?: string[]
    vertical_skill_key?: string
  }): Promise<Task> {
    const response = await this.client.post<Task>('/generate/image', data)
    return response.data
  }

  async generateVideoWithIdeas(data: {
    slots: (IdeaInput | null)[]
    prompt?: string
    model: string
    duration_seconds?: number
    aspect_ratio?: string
    resolution?: string
    product_id?: string
    include_product_screenshot?: boolean
    reference_asset_id?: string
    reference_asset_ids?: string[]
    vertical_skill_key?: string
    requirement_id?: string
  }): Promise<{ tasks: Task[] }> {
    const response = await this.client.post<{ tasks: Task[] }>('/generate/video', data)
    return response.data
  }

  // ---- Page access (UI display whitelist) ----
  async getPageAccess(): Promise<{ allowed_pages: string[] }> {
    const response = await this.client.get<{ allowed_pages: string[] }>('/users/me/page-access')
    return response.data
  }

  // ---- Material analytics ----
  private buildMaterialAnalyticsQuery(params: {
    start_date?: string
    end_date?: string
    author_email?: string
    author_email_exact?: string
    product_id?: string
    task_type?: string
    tool_id?: string
    is_downloaded?: boolean
    offset?: number
    limit?: number
  }): string {
    const query = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return
      query.set(key, String(value))
    })
    return query.toString()
  }

  async getMaterialAnalyticsSummary(params: {
    start_date?: string
    end_date?: string
    author_email?: string
    author_email_exact?: string
    product_id?: string
    task_type?: string
    tool_id?: string
  }): Promise<MaterialAnalyticsSummary> {
    const query = this.buildMaterialAnalyticsQuery(params)
    const response = await this.client.get<MaterialAnalyticsSummary>(
      `/material-analytics/summary${query ? `?${query}` : ''}`,
    )
    return response.data
  }

  async getMaterialAnalyticsAssets(params: {
    start_date?: string
    end_date?: string
    author_email?: string
    product_id?: string
    task_type?: string
    tool_id?: string
    is_downloaded?: boolean
    offset?: number
    limit?: number
  }): Promise<MaterialAnalyticsAssetListResponse> {
    const query = this.buildMaterialAnalyticsQuery(params)
    const response = await this.client.get<MaterialAnalyticsAssetListResponse>(
      `/material-analytics/assets${query ? `?${query}` : ''}`,
    )
    return response.data
  }

  async getMaterialAnalyticsChain(assetId: string): Promise<MaterialAnalyticsChainItem> {
    const response = await this.client.get<MaterialAnalyticsChainItem>(
      `/material-analytics/assets/${assetId}/chain`,
    )
    return response.data
  }

  async searchMaterialAnalyticsImages(files: File[]): Promise<MaterialAnalyticsImageSearchResult[]> {
    const formData = new FormData()
    files.forEach((file) => formData.append('files', file))
    const response = await this.client.post<MaterialAnalyticsImageSearchResult[]>(
      '/material-analytics/search-by-images',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return response.data
  }

  async getMaterialAnalyticsProducts(): Promise<MaterialAnalyticsProduct[]> {
    const response = await this.client.get<MaterialAnalyticsProduct[]>('/material-analytics/products')
    return response.data
  }

  async createMaterialAnalyticsProduct(data: CreateProductForm): Promise<MaterialAnalyticsProduct> {
    const response = await this.client.post<MaterialAnalyticsProduct>('/material-analytics/products', data)
    return response.data
  }

  async updateMaterialAnalyticsProduct(
    id: string,
    data: Partial<CreateProductForm>,
  ): Promise<MaterialAnalyticsProduct> {
    const response = await this.client.patch<MaterialAnalyticsProduct>(
      `/material-analytics/products/${id}`,
      data,
    )
    return response.data
  }

  async bindMaterialAnalyticsAssetProduct(
    assetId: string,
    productId: string | null,
  ): Promise<MaterialAnalyticsChainItem> {
    const response = await this.client.patch<MaterialAnalyticsChainItem>(
      `/material-analytics/assets/${assetId}/product`,
      { product_id: productId },
    )
    return response.data
  }

  // ---- Finance feedback ----
  async getFeedbackBatches(): Promise<FeedbackBatch[]> {
    const response = await this.client.get<FeedbackBatch[]>('/finance-feedback/batches')
    return response.data
  }

  async getFeedbackOverview(): Promise<FeedbackBatchStats[]> {
    const response = await this.client.get<FeedbackBatchStats[]>('/finance-feedback/overview')
    return response.data
  }

  async getFeedbackAssets(params: {
    date: string
    market: string
    type: string
  }): Promise<FeedbackBatchAssets> {
    const query = new URLSearchParams(params).toString()
    const response = await this.client.get<FeedbackBatchAssets>(`/finance-feedback/assets?${query}`)
    return response.data
  }

  async saveFeedbackReview(assetId: string, body: ReviewUpdate): Promise<FeedbackReview> {
    const response = await this.client.put<FeedbackReview>(
      `/finance-feedback/assets/${assetId}/review`,
      body,
    )
    return response.data
  }

  async recordFeedbackDownload(assetId: string): Promise<DownloadResult> {
    const response = await this.client.post<DownloadResult>(
      `/finance-feedback/assets/${assetId}/download`,
    )
    return response.data
  }

  async exportFeedback(params: { date: string; market: string; type: string }): Promise<Blob> {
    const query = new URLSearchParams(params).toString()
    const response = await this.client.get(`/finance-feedback/export?${query}`, {
      responseType: 'blob',
    })
    return response.data as Blob
  }

  // ---------------------------------------------------------------------
  // Daily batch review (internal)
  // ---------------------------------------------------------------------

  async listReviewBatches(offset = 0, limit = 50): Promise<PaginatedResponse<ReviewBatchSummary>> {
    const response = await this.client.get<PaginatedResponse<ReviewBatchSummary>>('/daily-batches', {
      params: { offset, limit },
    })
    return response.data
  }

  async getReviewBatch(batchId: string, country?: string): Promise<ReviewBatchDetail> {
    const response = await this.client.get<ReviewBatchDetail>(`/daily-batches/${batchId}`, {
      params: country ? { country } : undefined,
    })
    return response.data
  }

  async getReviewBatchReviews(batchId: string): Promise<BatchReviewsPayload> {
    const response = await this.client.get<BatchReviewsPayload>(`/daily-batches/${batchId}/reviews`)
    return response.data
  }

  /** 视频预加载的同源代理 URL（S3 直链对 fetch() 受 CORS 限制，走后端转发） */
  buildDailyBatchAssetRawUrl(assetId: string): string {
    return `${BASE_URL}/daily-batches/assets/${assetId}/raw`
  }

  async saveShootReview(
    shootId: string,
    variants: Record<string, VariantReviewInput>
  ): Promise<{ shoot_id: string; reviews: VariantFeedback[] }> {
    const response = await this.client.put<{ shoot_id: string; reviews: VariantFeedback[] }>(
      `/daily-batches/shoots/${shootId}/review`,
      { variants }
    )
    return response.data
  }

  async getBatchDownloadUrl(
    batchId: string,
    scope: 'all' | 'adopted' | 'country',
    country?: string
  ): Promise<string> {
    const response = await this.client.get<{ token: string }>(`/daily-batches/${batchId}/download-token`)
    const params = new URLSearchParams({ token: response.data.token, scope })
    if (country) params.set('country', country)
    return `${BASE_URL}/daily-batches/download/batch/${batchId}?${params.toString()}`
  }

  async exportBatchFeedbackJsonl(batchId: string): Promise<Blob> {
    const response = await this.client.get(`/daily-batches/${batchId}/feedback.jsonl`, {
      responseType: 'blob',
    })
    return response.data as Blob
  }

  async getShootDownloadUrl(batchId: string, shootId: string): Promise<string> {
    const response = await this.client.get<{ token: string }>(`/daily-batches/${batchId}/download-token`)
    const params = new URLSearchParams({ token: response.data.token })
    return `${BASE_URL}/daily-batches/download/shoot/${shootId}?${params.toString()}`
  }

  // ---- Finance reports ----
  async generateFinanceReports(body: {
    date: string
    creative_type: string
    markets?: string[]
  }): Promise<GenerateFinanceReportsResponse> {
    const response = await this.client.post<GenerateFinanceReportsResponse>(
      '/finance-report/generate',
      body,
    )
    return response.data
  }

  async getFinanceReports(params: {
    date: string
    type: string
    latest?: boolean
  }): Promise<FinanceReport[]> {
    const query = new URLSearchParams({ date: params.date, type: params.type })
    if (params.latest !== undefined) query.set('latest', String(params.latest))
    const response = await this.client.get<FinanceReport[]>(
      `/finance-report/reports?${query.toString()}`,
    )
    return response.data
  }
}

export const api = new ApiClient()
